<template>
  <div class="ncrm-home-board-metrics">
    <v-skeleton-loader v-if="loading" type="card@2" />
    <v-alert v-else-if="!items.length" type="info">
      {{ emptyMessage }}
    </v-alert>
    <div v-else>
      <v-row dense>
        <v-col
          v-for="item in items"
          :key="item.id"
          cols="12"
          :md="items.length === 1 ? 12 : 6"
          :xl="items.length === 1 ? 12 : items.length === 2 ? 6 : 4"
        >
          <v-card
            class="ncrm-home-board-metrics__card h-100"
            :class="[
              `ncrm-home-board-metrics__card--${resolvedHealth(item).status}`,
              { 'ncrm-home-board-metrics__card--single': items.length === 1 },
            ]"
            variant="outlined"
            role="button"
            tabindex="0"
            :aria-label="buildAriaLabel(item)"
            @click="openItem(item.id)"
            @keydown.enter.prevent="openItem(item.id)"
            @keydown.space.prevent="openItem(item.id)"
          >
            <div class="ncrm-home-board-metrics__card-content">

              <!-- Header -->
              <div class="ncrm-home-board-metrics__header">
                <div class="d-flex align-center ga-2 min-width-0 flex-grow-1">
                  <span
                    class="ncrm-home-board-metrics__health-dot"
                    :class="`ncrm-home-board-metrics__health-dot--${resolvedHealth(item).status}`"
                    :title="resolvedHealth(item).label"
                  />
                  <span class="ncrm-home-board-metrics__title" :title="item.name">
                    {{ item.name }}
                  </span>
                </div>
                <v-chip
                  v-if="resolvedBadgeText(item)"
                  size="x-small"
                  color="quaternary"
                  class="ncrm-home-board-metrics__badge"
                >
                  {{ resolvedBadgeText(item) }}
                </v-chip>
              </div>

              <!-- Primary metric tile -->
              <div v-if="item.primaryLabel" class="ncrm-home-board-metrics__metric-tile ncrm-home-board-metrics__metric-tile--primary">
                <span class="ncrm-home-board-metrics__primary-value">
                  {{ formatValue(item.primaryValue) }}
                </span>
                <span class="ncrm-home-board-metrics__primary-label">
                  {{ item.primaryLabel }}
                </span>
                <div
                  v-if="resolvedProgress(item)"
                  class="ncrm-home-board-metrics__progress"
                >
                  <v-progress-linear
                    :model-value="resolvedProgress(item).percent"
                    color="primary"
                    height="4"
                    rounded
                    bg-color="surface-variant"
                  />
                  <div class="ncrm-home-board-metrics__progress-label">
                    {{ resolvedProgress(item).label }}
                  </div>
                </div>
              </div>

              <!-- Secondary metrics grid -->
              <div
                v-if="resolvedMetrics(item).length"
                class="ncrm-home-board-metrics__metrics"
              >
                <div
                  v-for="(metric, idx) in resolvedMetrics(item)"
                  :key="metric.key"
                  class="ncrm-home-board-metrics__stat"
                  :class="{ 'ncrm-home-board-metrics__stat--divider': idx > 0 }"
                >
                  <div class="d-flex align-center ga-1 mb-1">
                    <v-icon
                      v-if="metric.icon"
                      size="12"
                      :color="metric.color || 'primary'"
                      style="opacity:0.8"
                    >
                      {{ metric.icon }}
                    </v-icon>
                    <span class="ncrm-home-board-metrics__stat-label">
                      {{ metric.label }}
                    </span>
                  </div>
                  <span
                    class="ncrm-home-board-metrics__stat-value"
                    :class="`ncrm-home-board-metrics__stat-value--${metric.color || 'default'}`"
                  >
                    {{ formatValue(metric.value) }}
                  </span>
                </div>
              </div>

            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { formatDisplayNumber } from "@/Utile/displayFormat.mjs";
const TOTAL_METRIC_KEYS = new Set([
  "total_deals",
  "entry_count",
  "total_entries",
  "total",
]);

export default {
  name: "NcrmHomeBoardMetrics",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    emptyMessage: {
      type: String,
      default: "",
    },
    eyebrowLabel: {
      type: String,
      default: "",
    },
    openLabel: {
      type: String,
      default: "",
    },
  },
  emits: ["open"],
  methods: {
    formatValue(value) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return formatDisplayNumber(parsed);
      }
      const normalized = String(value ?? "").trim();
      return normalized || "-";
    },
    resolvedBadgeText(item = {}) {
      const directText = String(item?.badgeText || "").trim();
      if (directText) return directText;

      const badgeLabel = String(item?.badgeLabel || "").trim();
      const badgeValue = item?.badgeValue;
      if (
        badgeLabel &&
        badgeValue !== null &&
        badgeValue !== undefined &&
        String(badgeValue).trim() !== ""
      ) {
        return `${badgeLabel}: ${this.formatValue(badgeValue)}`;
      }
      return badgeLabel;
    },
    buildAriaLabel(item = {}) {
      const normalizedName = String(item?.name || "").trim();
      if (!this.openLabel) return normalizedName || "board";
      return normalizedName
        ? `${this.openLabel} ${normalizedName}`
        : this.openLabel;
    },
    /**
     * Returns secondary metrics for item, excluding the total metric used
     * as the progress-bar denominator to avoid duplication.
     */
    resolvedMetrics(item = {}) {
      const metrics = Array.isArray(item?.metrics) ? item.metrics : [];
      const progress = this.resolvedProgress(item);
      if (!progress) return metrics;
      return metrics.filter((metric) => metric.key !== progress.totalKey);
    },
    resolvedHealth(item = {}) {
      const metrics = Array.isArray(item?.metrics) ? item.metrics : [];
      const byKey = (key) => {
        const match = metrics.find((metric) => metric?.key === key);
        return match ? Number(match.value) || 0 : 0;
      };
      const overdue = byKey("overdue_followups");
      const touched = byKey("touched_today");

      if (overdue > 0) {
        return {
          status: "at-risk",
          label: this.$t("ncrm.boardMetrics.health.atRisk"),
        };
      }
      if (touched > 0) {
        return {
          status: "active",
          label: this.$t("ncrm.boardMetrics.health.active"),
        };
      }
      return {
        status: "dormant",
        label: this.$t("ncrm.boardMetrics.health.dormant"),
      };
    },
    resolvedProgress(item = {}) {
      const primary = Number(item?.primaryValue);
      if (!Number.isFinite(primary) || primary < 0) return null;

      const metrics = Array.isArray(item?.metrics) ? item.metrics : [];
      const totalMetric = metrics.find((metric) =>
        TOTAL_METRIC_KEYS.has(metric?.key)
      );
      if (!totalMetric) return null;

      const total = Number(totalMetric.value);
      if (!Number.isFinite(total) || total <= 0) return null;

      const percent = Math.max(0, Math.min(100, (primary / total) * 100));
      return {
        percent,
        totalKey: totalMetric.key,
        label: this.$t("ncrm.boardMetrics.progress", {
          percent: Math.round(percent),
          total: this.formatValue(total),
          totalLabel: totalMetric.label || "",
        }),
      };
    },
    openItem(itemId) {
      const normalizedId = Number(itemId || 0);
      if (!normalizedId) return;
      this.$emit("open", normalizedId);
    },
  },
};
</script>

<style scoped>
/* ── Card shell ─────────────────────────────────────────────────────────── */
.ncrm-home-board-metrics__card {
  position: relative;
  overflow: hidden;
  border-radius: var(--app-radius-base);
  border-color: var(--ncrm-color-border-default);
  background: var(--ncrm-color-bg-card);
  box-shadow: 0 2px 6px rgba(var(--v-theme-on-surface), 0.04);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  cursor: pointer;
}

.ncrm-home-board-metrics__card:hover,
.ncrm-home-board-metrics__card:focus-visible {
  border-color: var(--ncrm-color-primary-border);
  box-shadow: 0 6px 16px rgba(var(--v-theme-primary), 0.12);
  transform: none;
}

.ncrm-home-board-metrics__card:focus-visible {
  outline: none;
}

/* Left accent stripe reflects health */
.ncrm-home-board-metrics__card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 3px;
  background: rgba(var(--v-theme-on-surface), 0.12);
  transition: background 0.15s ease;
}
.ncrm-home-board-metrics__card--active::before {
  background: rgb(var(--v-theme-success));
}
.ncrm-home-board-metrics__card--at-risk::before {
  background: rgb(var(--v-theme-error));
}
.ncrm-home-board-metrics__card--dormant::before {
  background: rgba(var(--v-theme-on-surface), 0.18);
}

/* ── Card content wrapper ───────────────────────────────────────────────── */
.ncrm-home-board-metrics__card-content {
  display: flex;
  flex-direction: column;
  padding: 12px 14px 12px;
  height: 100%;
}

/* ── Header ─────────────────────────────────────────────────────────────── */
.ncrm-home-board-metrics__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.ncrm-home-board-metrics__health-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(var(--v-theme-on-surface), 0.25);
  box-shadow: 0 0 0 2px rgba(var(--v-theme-on-surface), 0.04);
}
.ncrm-home-board-metrics__health-dot--active {
  background: rgb(var(--v-theme-success));
  box-shadow: 0 0 0 2px rgba(var(--v-theme-success), 0.18);
}
.ncrm-home-board-metrics__health-dot--at-risk {
  background: rgb(var(--v-theme-error));
  box-shadow: 0 0 0 2px rgba(var(--v-theme-error), 0.18);
  animation: ncrm-home-board-metrics__pulse 1.8s ease-in-out infinite;
}
.ncrm-home-board-metrics__health-dot--dormant {
  background: rgba(var(--v-theme-on-surface), 0.25);
}

@keyframes ncrm-home-board-metrics__pulse {
  0%, 100% {
    box-shadow: 0 0 0 2px rgba(var(--v-theme-error), 0.2);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(var(--v-theme-error), 0.08);
  }
}

.ncrm-home-board-metrics__title {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.25;
  color: rgb(var(--v-theme-on-surface));
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.ncrm-home-board-metrics__badge {
  flex: 0 0 auto;
  max-width: 50%;
  font-size: 0.7rem;
}

/* ── Primary metric tile ────────────────────────────────────────────────── */
.ncrm-home-board-metrics__metric-tile {
  margin-bottom: 8px;
}

.ncrm-home-board-metrics__metric-tile--primary {
  margin: 2px 0 10px;
}

.ncrm-home-board-metrics__primary-value {
  display: block;
  font-size: clamp(1.2rem, 1rem + 0.52vw, 1.58rem);
  font-weight: 700;
  line-height: 1;
  color: rgb(var(--v-theme-primary));
  letter-spacing: -0.01em;
}

.ncrm-home-board-metrics__primary-label {
  display: block;
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.72);
  font-weight: 500;
  margin-top: 2px;
}

.ncrm-home-board-metrics__progress {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.ncrm-home-board-metrics__progress-label {
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* ── Secondary metrics grid ─────────────────────────────────────────────── */
.ncrm-home-board-metrics__metrics {
  display: flex;
  align-items: stretch;
  gap: 0;
  margin-top: auto;
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(var(--v-theme-surface), 0.98);
}

.ncrm-home-board-metrics__stat {
  flex: 1 1 0;
  min-width: 0;
  padding: 7px 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.ncrm-home-board-metrics__stat--divider {
  border-left: 1px solid var(--ncrm-color-border-default);
}

.ncrm-home-board-metrics__stat-label {
  font-size: 0.68rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.2;
}

.ncrm-home-board-metrics__stat-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.ncrm-home-board-metrics__stat-value--error {
  color: rgb(var(--v-theme-error));
}
.ncrm-home-board-metrics__stat-value--success {
  color: rgb(var(--v-theme-success));
}
.ncrm-home-board-metrics__stat-value--warning {
  color: rgb(var(--v-theme-warning));
}
.ncrm-home-board-metrics__stat-value--info {
  color: rgb(var(--v-theme-info));
}

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 600px) {
  .ncrm-home-board-metrics__metrics {
    flex-wrap: wrap;
  }
  .ncrm-home-board-metrics__stat {
    flex: 1 1 45%;
  }
  .ncrm-home-board-metrics__badge {
    max-width: 60%;
  }
}
</style>
